/**
 * Topic names (realtime-service `app/topics.py`, design/07 §9.1 hub; §9.2 per-service streams).
 *
 * Hub topics — a message says "something changed", the screen refetches:
 * - `staff:<area>` (permission of the area) · `pos:register:<id>` · `customer:<id>` (self)
 * - `staff-self:<staffId>` (the staff member themself: their own preferences, T01-17)
 * - `order:<number>` (tracking link token, R20) · `consignor:<id>` (portal)
 * - `public:variant:<id>` · `public:collection:<handle>` · `public:cms` (no sign-in, no PII)
 *
 * Per-service streams (ADR-0009, wrapped by `streams.ts` behind the same `subscribe()`), carrying
 * a payload at once:
 * - `inventory.changed:<variantId>` (or `inventory.changed:*`) — inventory-service, POS/admin
 * - `inbox` — notification-service badge `{summary, item}`
 * - `order.tracking:<number>` — order-service tracking page snapshot (token)
 */

/** Same rule as the hub (`PARAM_RE`): ids, order numbers, handles — no commas. */
const PARAM = /^[A-Za-z0-9][A-Za-z0-9_.-]{0,127}$/;

/** Staff areas of the hub registry (`STAFF_TOPICS`). */
export const STAFF_AREAS = [
  "orders",
  "payments",
  "inventory",
  "catalog",
  "consignment",
  "cms",
  "tax",
  "pos",
  "customers",
  "inbox",
  "staff",
  "audit",
  "notifications",
] as const;
export type StaffArea = (typeof STAFF_AREAS)[number];

const HUB_KEYED = [
  "pos:register:",
  "customer:",
  "staff-self:",
  "order:",
  "receipt:",
  "consignor:",
  "public:variant:",
  "public:collection:",
];

export const CMS_TOPIC = "public:cms";
export const INBOX_TOPIC = "inbox";
export const INVENTORY_PREFIX = "inventory.changed:";
export const TRACKING_PREFIX = "order.tracking:";

/** `true` for a valid id / order number / handle of a keyed topic. */
export function isTopicParam(param: string | null | undefined): param is string {
  return typeof param === "string" && PARAM.test(param);
}

function keyed(prefix: string, param: string | null | undefined): string | null {
  return isTopicParam(param) ? `${prefix}${param}` : null;
}

/** `true` when the hub registry knows this topic (anything else is refused with 400). */
export function isHubTopic(topic: string): boolean {
  if (topic === CMS_TOPIC) {
    return true;
  }
  if (topic.startsWith("staff:")) {
    return (STAFF_AREAS as readonly string[]).includes(topic.slice(6));
  }
  const prefix = HUB_KEYED.find((p) => topic.startsWith(p));
  return prefix !== undefined && isTopicParam(topic.slice(prefix.length));
}

export const staffTopic = (area: StaffArea): string => `staff:${area}`;
/** One register's screen (POS device / POS staff token of that register). */
export const registerTopic = (registerId: string | null | undefined) =>
  keyed("pos:register:", registerId);
/** The signed-in customer's own pages. */
export const customerTopic = (customerId: string | null | undefined) =>
  keyed("customer:", customerId);
/** The signed-in staff member's own preferences (their other computers follow — T01-17). */
export const staffSelfTopic = (staffId: string | null | undefined) => keyed("staff-self:", staffId);
/** Tracking page (hub; proven with the link's `orderToken`). */
export const orderTopic = (orderNumber: string | null | undefined) => keyed("order:", orderNumber);
/** Digital receipt page `/r/<receiptNo>?t=` (hub; proven with the link's `t` — T14-16). */
export const receiptTopic = (receiptNo: string | null | undefined) => keyed("receipt:", receiptNo);
/** Consignor portal (the signed-in consignor's own id). */
export const consignorTopic = (consignorId: string | null | undefined) =>
  keyed("consignor:", consignorId);
/** Product page / tile / bag line: price or availability of this variant changed. */
export const variantTopic = (variantId: string | null | undefined) =>
  keyed("public:variant:", variantId);
/** Collection page: the collection itself was edited. */
export const collectionTopic = (handle: string | null | undefined) =>
  keyed("public:collection:", handle);
/** inventory-service stream (ADR-0009): availability payload at once; `*` = every variant. */
export const inventoryTopic = (variantId: string | null | undefined) =>
  variantId === "*" ? `${INVENTORY_PREFIX}*` : keyed(INVENTORY_PREFIX, variantId);
/** order-service tracking stream (ADR-0009): snapshot payload (token of the link). */
export const trackingTopic = (orderNumber: string | null | undefined) =>
  keyed(TRACKING_PREFIX, orderNumber);

/** Topics of a list of ids, invalid ones dropped, each once, in order. */
export function topicsOf(
  make: (param: string) => string | null,
  params: Iterable<string | null | undefined>,
): string[] {
  const out = new Set<string>();
  for (const param of params) {
    const topic = param ? make(param) : null;
    if (topic) {
      out.add(topic);
    }
  }
  return [...out];
}
